import { z } from "zod"

import { ResearchCategory } from "@/constants/terminal/research-category"
import { ScoreBreakdownKind } from "@/constants/terminal/score-breakdown-kind"
import { ScoreInputKind } from "@/constants/terminal/score-input-kind"
import { agedWeight, fullYearsBetween } from "@/terminal/aged-weight"

const scoreRowFields = {
  scoreInputKey: z.string().min(1),
  label: z.string().min(1),
  scoreBefore: z.number().min(0).max(100),
  pointAdjustment: z.number().min(-100).max(100),
  minimumScore: z.number().int().min(0).max(100).nullable(),
  maximumScore: z.number().min(0).max(100).nullable(),
  scoreAfter: z.number().min(0).max(100),
}

const scoreBreakdownRowSchema = z.discriminatedUnion("kind", [
  z
    .object({
      kind: z.literal(ScoreInputKind.DETECTION),
      ...scoreRowFields,
      detectionId: z.string().uuid(),
    })
    .strict(),
  z
    .object({
      kind: z.literal(ScoreInputKind.AGGREGATE),
      ...scoreRowFields,
      sourceRecordIds: z.array(z.string().uuid()).min(1),
    })
    .strict(),
  z
    .object({
      kind: z.literal(ScoreInputKind.POLICY_CONSTRAINT),
      ...scoreRowFields,
      sourceRecordIds: z.array(z.string().uuid()),
      sourceCheckIds: z.array(z.string().uuid()),
      researchSufficiencyCategories: z.array(z.nativeEnum(ResearchCategory)),
    })
    .strict(),
])

// A Detection's key: the Score Input key of its Score Breakdown row, and how
// two Files' Boards join.
export const detectionIdentity = ({
  detectionTypeKey,
  detectionKey,
}: {
  detectionTypeKey: string
  detectionKey: string
}) => `${detectionTypeKey}:${detectionKey}`

// ADR-0129 (straight-line aging): an active Detection weighs by its newest dated
// event, and an undated one keeps its initial weight until a date arrives. A
// Detection that is not active weighs nothing.
export const detectionWeight = ({
  isActive,
  eventTimes,
  cutoff,
  annualDecay,
}: {
  isActive: boolean
  eventTimes: number[]
  cutoff: number
  annualDecay: number
}) => {
  if (!isActive) return 0
  if (eventTimes.length === 0) return 1
  return agedWeight(Math.max(...eventTimes), cutoff, annualDecay)
}

// An adverse line fades toward 100 as its Detection's weight does, and a
// Detection with no weight left sets none.
export const fadedLine = (initialLine: number | null, weight: number) =>
  initialLine === null || weight === 0
    ? null
    : 100 - (100 - initialLine) * weight

// What each deduction cap has taken so far: a type's own cap by its type key
// and a combined cap (ADR-0111) by its own key. Each call returns the points
// still inside the cap.
export const createDeductionBudget = () => {
  const deductionByCap = new Map<string, number>()
  return (
    pointAdjustment: number,
    capKey: string,
    maximumDeduction: number
  ) => {
    const used = deductionByCap.get(capKey) ?? 0
    const capped = -Math.min(
      -pointAdjustment,
      Math.max(0, maximumDeduction - used)
    )
    deductionByCap.set(capKey, used - capped)
    return capped
  }
}

// ADR-0123: a volume-scaled Detection's points. Each supporting record
// deducts `pointAdjustment` at its own decay weight while the Customer Harm
// Rate reads at most `referenceReportCount` customer reports, and that
// count's share of the reports above it, so ten times the reviews takes ten
// times the suits. `assess` and the Policy Tuner both score through it.
export const volumeScaledPoints = ({
  pointAdjustment,
  recordWeights,
  reportCount,
  referenceReportCount,
}: {
  pointAdjustment: number
  recordWeights: number[]
  reportCount: number
  referenceReportCount: number
}) =>
  pointAdjustment *
  Math.min(1, referenceReportCount / Math.max(1, reportCount)) *
  recordWeights.reduce((total, weight) => total + weight, 0)

export const applyScoreRow = (
  score: number,
  {
    pointAdjustment,
    minimumScore,
    maximumScore,
  }: {
    pointAdjustment: number
    minimumScore: number | null
    maximumScore: number | null
  }
) =>
  Math.min(
    100,
    Math.max(
      0,
      Math.min(
        maximumScore ?? Number.POSITIVE_INFINITY,
        Math.max(
          minimumScore ?? Number.NEGATIVE_INFINITY,
          score + pointAdjustment
        )
      )
    )
  )

// The clean years and their points: the full years back from the cutoff to
// the later of the business start and the newest finding. An undated finding
// leaves no clean year, and an unsearched category or an unknown start earns
// nothing. ADR-0146 (a score rises above 75 on two kinds of evidence);
// `assess` and the Policy Tuner both score through it.
export const cleanYearCredit = (
  {
    isSearched,
    businessStartDay,
    findingDates,
  }: {
    isSearched: boolean
    businessStartDay: string | null
    findingDates: (string | null)[]
  },
  cutoff: string,
  {
    pointsPerYear,
    maximumYears,
  }: { pointsPerYear: number; maximumYears: number }
) => {
  const years =
    !isSearched || businessStartDay === null || findingDates.includes(null)
      ? 0
      : fullYearsBetween(
          Math.max(
            Date.parse(businessStartDay),
            ...findingDates.map((date) => Date.parse(date!))
          ),
          Date.parse(cutoff)
        )
  return {
    years,
    points: Math.min(years, maximumYears) * pointsPerYear,
  }
}

const scoredBreakdownSchema = z
  .object({
    kind: z.literal(ScoreBreakdownKind.SCORED),
    baselineScore: z.number().int().min(0).max(100),
    rows: z.array(scoreBreakdownRowSchema),
    finalScore: z.number().int().min(0).max(100),
  })
  .strict()
  .superRefine((breakdown, context) => {
    let expectedScore = breakdown.baselineScore
    const scoreInputKeys = breakdown.rows.map(
      ({ scoreInputKey }) => scoreInputKey
    )

    for (const [index, row] of breakdown.rows.entries()) {
      // ADR-0129 (sum, then limit): a Score Input only moves the score, and
      // each limit is its own policy constraint row after them. A hard limit
      // holds the score with exactly one limit and no points; a soft one
      // moves it by points alone (ADR-0157).
      const limitCount = [row.minimumScore, row.maximumScore].filter(
        (limit) => limit !== null
      ).length
      const isHardLimit =
        row.kind === ScoreInputKind.POLICY_CONSTRAINT &&
        row.pointAdjustment === 0
      if (limitCount !== (isHardLimit ? 1 : 0)) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["rows", index],
          message:
            "Only a hard policy constraint row limits the score, with exactly one limit and no points",
        })
      }
      const scoreAfter = applyScoreRow(expectedScore, row)
      if (row.scoreBefore !== expectedScore || row.scoreAfter !== scoreAfter) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["rows", index],
          message: "Score row does not reconcile with the preceding score",
        })
      }
      expectedScore = row.scoreAfter
    }

    if (new Set(scoreInputKeys).size !== scoreInputKeys.length) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["rows"],
        message: "Each Score Input must appear once",
      })
    }
    if (breakdown.finalScore !== Math.round(expectedScore)) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["finalScore"],
        message: "Final score must match the complete Score Breakdown",
      })
    }
  })

export const ScoreBreakdownSchema = z.union([
  scoredBreakdownSchema,
  z
    .object({
      kind: z.literal(ScoreBreakdownKind.UNCLEAR),
      reasons: z.array(z.string().min(1)).min(1),
    })
    .strict(),
])

export type ScoreBreakdown = z.infer<typeof ScoreBreakdownSchema>
